import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProgressBar from './ProgressBar';
import { ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import QUESTIONS from './questionsData';
import { SECTION_FINAL_WEIGHTS } from './resultsData';

const glassCard = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.8)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
};

const SECTION_THEMES = {
  1: { pillBg: '#dbeafe', pillText: '#1d4ed8', accent: '#3b82f6', name: 'Customer Intelligence' },
  2: { pillBg: '#ede9fe', pillText: '#6d28d9', accent: '#7c3aed', name: 'Communication Execution' },
  3: { pillBg: '#fef3c7', pillText: '#92400e', accent: '#f59e0b', name: 'Omni-Channel Loyalty & Offers' },
};

const OPTION_COLOURS = {
  A: { border: '#fca5a5', borderSelected: '#ef4444', letterBg: '#fee2e2', letterText: '#dc2626', selectedBg: '#fef2f2' },
  B: { border: '#fcd34d', borderSelected: '#f59e0b', letterBg: '#fef3c7', letterText: '#92400e', selectedBg: '#fffbeb' },
  C: { border: '#93c5fd', borderSelected: '#3b82f6', letterBg: '#dbeafe', letterText: '#1d4ed8', selectedBg: '#eff6ff' },
  D: { border: '#86efac', borderSelected: '#22c55e', letterBg: '#dcfce7', letterText: '#166534', selectedBg: '#f0fdf4' },
};

const SECTION_RANGES = [
  { section: 1, start: 0, end: 2 },
  { section: 2, start: 3, end: 6 },
  { section: 3, start: 7, end: 8 },
];

/* New scoring: section score = sum(score * weight / 100) → 0-10 scale */
const computeScores = (answers) => {
  const sections = SECTION_RANGES.map(({ section, start, end }) => {
    const qs = QUESTIONS.filter((q) => q.index >= start && q.index <= end);
    const rawScore = qs.reduce((sum, q) => {
      const ans = answers[q.index];
      const score = ans ? ans.score : 0;
      return sum + (score * q.weight / 100);
    }, 0);
    const sectionScore = Math.round(rawScore * 10) / 10;
    return { section, name: SECTION_THEMES[section].name, score: sectionScore };
  });

  // Final score 0-100: weighted sum where each section is 0-10
  const finalScore = Math.round(
    sections[0].score * (SECTION_FINAL_WEIGHTS[0] * 10) +
    sections[1].score * (SECTION_FINAL_WEIGHTS[1] * 10) +
    sections[2].score * (SECTION_FINAL_WEIGHTS[2] * 10)
  );

  return {
    sections: sections.map((s) => s.score),
    sectionDetails: sections,
    final: finalScore,
  };
};

const getSectionNumber = (qIdx) => {
  for (const r of SECTION_RANGES) {
    if (qIdx >= r.start && qIdx <= r.end) return r.section;
  }
  return 1;
};

const getShortHeading = (text) => {
  const dashIdx = text.indexOf(' — ');
  if (dashIdx > 0 && dashIdx < 100) return text.substring(0, dashIdx);
  const periodIdx = text.indexOf('. ');
  if (periodIdx > 0 && periodIdx < 120) return text.substring(0, periodIdx + 1);
  if (text.length > 90) return text.substring(0, 87) + '...';
  return text;
};

const OptionCard = ({ option, isSelected, onSelect }) => {
  const colors = OPTION_COLOURS[option.letter];
  const shortHeading = getShortHeading(option.text);

  return (
    <motion.button
      onClick={onSelect}
      whileHover={{ scale: 1.015, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className="w-full text-left outline-none focus:outline-none h-full cursor-pointer"
      data-testid={`option-${option.letter}`}
    >
      <div
        className="relative flex items-start gap-3 rounded-xl overflow-hidden transition-all duration-200 h-full"
        style={{
          border: isSelected ? `2px solid ${colors.borderSelected}` : '1.5px solid #e2e8f0',
          background: isSelected ? colors.selectedBg : 'rgba(255,255,255,0.9)',
          padding: isSelected ? '13px 13px 13px 17px' : '14px 14px 14px 18px',
          boxShadow: isSelected ? `0 4px 16px ${colors.borderSelected}18` : '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl" style={{ backgroundColor: isSelected ? colors.borderSelected : colors.border }} />
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: isSelected ? `${colors.borderSelected}15` : colors.letterBg }}>
          {isSelected ? (
            <CheckCircle2 className="w-5 h-5" style={{ color: colors.borderSelected }} />
          ) : (
            <span className="text-[13px] font-bold" style={{ color: colors.letterText }}>{option.letter}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[13px] font-semibold leading-snug ${isSelected ? 'text-[#0f172a]' : 'text-[#334155]'}`}>{shortHeading}</p>
          <p className="text-[11px] text-[#94a3b8] leading-relaxed mt-1.5 italic line-clamp-2">{option.example}</p>
        </div>
      </div>
    </motion.button>
  );
};

const QuestionsScreen = ({ onNext, onBack, answers, setAnswers }) => {
  const [currentQ, setCurrentQ] = useState(0);
  const [direction, setDirection] = useState(1);
  const autoAdvanceTimer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  const total = QUESTIONS.length;
  const safeQ = Math.min(currentQ, total - 1);
  const q = QUESTIONS[safeQ];
  const section = getSectionNumber(safeQ);
  const theme = SECTION_THEMES[section];
  const progress = ((safeQ + (answers[safeQ] !== undefined ? 1 : 0)) / total) * 100;
  const isAnswered = answers[safeQ] !== undefined;
  const isLast = safeQ === total - 1;
  const isFirst = safeQ === 0;

  const handleSelect = useCallback((optionIdx) => {
    const option = q.options[optionIdx];
    const qIndex = q.index;
    setAnswers((prev) => ({
      ...prev,
      [qIndex]: { letter: option.letter, score: option.score },
    }));

    // Auto-advance after short delay
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    autoAdvanceTimer.current = setTimeout(() => {
      if (safeQ >= total - 1) {
        // Last question — compute and submit
        const updatedAnswers = { ...answers, [qIndex]: { letter: option.letter, score: option.score } };
        onNext(computeScores(updatedAnswers));
      } else {
        setDirection(1);
        setCurrentQ((prev) => prev + 1);
      }
    }, 400);
  }, [q, setAnswers, safeQ, total, answers, onNext]);

  const handleBack = () => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    if (isFirst) {
      onBack();
    } else {
      setDirection(-1);
      setCurrentQ((prev) => prev - 1);
    }
  };

  const slideVariants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 30 : -30 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -30 : 30 }),
  };

  return (
    <div className="flex flex-col items-center w-full" data-testid="questions-screen">
      <div className="w-full mb-5">
        <ProgressBar progress={progress} />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs font-medium text-[#64748b]">Question {safeQ + 1} of {total}</span>
          <span className="text-xs font-semibold text-[#3b82f6]">{Math.round(progress)}% complete</span>
        </div>
      </div>

      <div className="w-full rounded-2xl overflow-hidden" style={glassCard}>
        <motion.div
          key={`section-bar-${section}`}
          initial={{ opacity: 0.7 }}
          animate={{ opacity: 1 }}
          className="h-14 flex items-center justify-between px-6 sm:px-8"
          style={{ background: `${theme.pillBg}88`, borderBottom: '1px solid rgba(0,0,0,0.04)' }}
        >
          <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ backgroundColor: theme.pillBg, color: theme.pillText }}>
            Section {section} of 3 · {theme.name}
          </span>
          <span className="text-xs font-medium text-[#94a3b8]">Q{safeQ + 1} of {total}</span>
        </motion.div>

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={safeQ}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="px-6 sm:px-8 pt-7 pb-5">
              <h2 className="text-lg font-bold text-[#0f172a] leading-snug">{q.text}</h2>
              {q.note && <p className="text-sm text-[#64748b] mt-2 leading-relaxed">{q.note}</p>}
            </div>
            <div className="px-6 sm:px-8 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {q.options.map((option, idx) => (
                <OptionCard
                  key={option.letter}
                  option={option}
                  isSelected={answers[safeQ]?.letter === option.letter}
                  onSelect={() => handleSelect(idx)}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between px-6 sm:px-8 py-5" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 text-[13px] font-medium text-[#94a3b8] hover:text-[#0f172a] transition-colors cursor-pointer"
            data-testid="questions-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <motion.button
            onClick={() => {
              if (!isAnswered) return;
              if (isLast) onNext(computeScores(answers));
              else { setDirection(1); setCurrentQ((prev) => prev + 1); }
            }}
            disabled={!isAnswered}
            whileHover={isAnswered ? { scale: 1.03 } : {}}
            whileTap={isAnswered ? { scale: 0.97 } : {}}
            className={`flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-full transition-all ${
              isAnswered
                ? 'bg-gradient-to-r from-[#3b82f6] to-[#2563eb] text-white shadow-md cursor-pointer'
                : 'bg-[#e2e8f0] text-[#94a3b8] cursor-not-allowed'
            }`}
            data-testid="questions-next-button"
          >
            {isLast ? 'See my results' : 'Next'}
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default QuestionsScreen;
